// src/pages/usuarios/UsuariosCrear.jsx
import React, { useState, useEffect } from "react";
import { Card, Form, Input, Button, Space, DatePicker, Select, Typography, Divider, Spin } from "antd";
import { SaveOutlined, ArrowLeftOutlined, ReloadOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import Usuarios from "../../../../../service/Usuarios";
import Recintos from "../../../../../service/Recintos";
import { useNotify, useWithNotify } from "../../../../../providers/NotificationProvider";
import TextArea from "antd/es/input/TextArea";
import Roles from "../../../../../service/Roles";

const { Title } = Typography;

// Los roles se piden a la API: un rol creado desde «Roles y permisos» tiene que
// aparecer acá sin recompilar. `cliente` no se ofrece, es el rol del registro.

const GENDERS = [
    { label: "Masculino", value: "masculino" },
    { label: "Femenino", value: "femenino" },
    { label: "Otro", value: "otro" },
];

// Comunas de Chile (RM)
const COMMUNES = [
    "Cerrillos", "Cerro Navia", "Conchalí", "El Bosque", "Estación Central", "Huechuraba", "Independencia",
    "La Cisterna", "La Florida", "La Granja", "La Pintana", "La Reina", "Las Condes", "Lo Barnechea", "Lo Espejo",
    "Lo Prado", "Macul", "Maipú", "Ñuñoa", "Pedro Aguirre Cerda", "Peñalolén", "Providencia", "Pudahuel",
    "Quilicura", "Quinta Normal", "Recoleta", "Renca", "San Joaquín", "San Miguel", "San Ramón", "Santiago",
    "Vitacura", "Puente Alto", "Pirque", "San José de Maipo", "Colina", "Lampa", "Tiltil", "San Bernardo",
    "Buin", "Calera de Tango", "Paine"
].sort().map(c => ({ label: c, value: c.toLowerCase() }));

// Generar contraseña cumpliendo requisitos
const generatePassword = (length = 12) => {
    const upper = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
    const lower = "abcdefghijklmnopqrstuvwxyz";
    const nums = "0123456789";
    const special = "!@#$%^&*()-_=+[]{};:,.<>/?";
    const all = upper + lower + nums + special;

    let pwd = [
        upper[Math.floor(Math.random() * upper.length)],
        lower[Math.floor(Math.random() * lower.length)],
        nums[Math.floor(Math.random() * nums.length)],
        special[Math.floor(Math.random() * special.length)],
    ];

    for (let i = pwd.length; i < length; i++) {
        pwd.push(all[Math.floor(Math.random() * all.length)]);
    }

    for (let i = pwd.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [pwd[i], pwd[j]] = [pwd[j], pwd[i]];
    }
    return pwd.join("");
};

const formatRutSoft = (raw) => {
    if (raw == null) return "";
    let v = String(raw).replace(/\./g, "").replace(/-/g, "").replace(/\s/g, "").toUpperCase();
    v = v.replace(/[^0-9K]/g, "");
    if (!v) return "";

    let body = v;
    let dv = "";
    if (v.length > 1) {
        body = v.slice(0, -1);
        dv = v.slice(-1);
    }

    const rev = body.split("").reverse().join("");
    const chunks = rev.match(/.{1,3}/g) || [];
    let formattedBody = chunks.join(".").split("").reverse().join("");

    return dv ? `${formattedBody}-${dv}` : formattedBody;
};

export default function UsuariosCrear() {
    const [form] = Form.useForm();
    const withNotify = useWithNotify();
    const notify = useNotify();
    const [saving, setSaving] = useState(false);
    const [loadingRecintos, setLoadingRecintos] = useState(false);
    const [recintos, setRecintos] = useState([]);
    const roleWatch = Form.useWatch("role", form);
    const [roles, setRoles] = useState([]);

    const ROLES = roles
        .filter(r => r.key !== "cliente")
        .map(r => ({
            label: r.nombre,
            value: r.key,
            // Avisar cuando el rol no entra al panel (es el caso de `funcionario`,
            // que existe solo para abrir el torniquete) evita la sorpresa de crear
            // una cuenta que no puede iniciar sesión en la administración.
            title: r.entraAlPanel ? undefined : "No tiene acceso al panel de administración",
        }));

    const showArea = roleWatch === "jefe_recinto";

    // 🆕 Cargar recintos al montar el componente
    useEffect(() => {
        loadRecintos();
        Roles.asignables()
            .then(setRoles)
            .catch(err => console.error("No se pudieron cargar los roles asignables:", err));
    }, []);

    const loadRecintos = async () => {
        try {
            setLoadingRecintos(true);
            const response = await Recintos.List({ limit: 0, estado: "activo" });
            // Mapear recintos a opciones del select
            const options = response.data.map(recinto => ({
                label: recinto.nombre + (recinto.comuna ? ` - ${recinto.comuna}` : ""),
                value: recinto.nombre
            }));

            setRecintos(options);
        } catch (error) {
            console.error("Error cargando recintos:", error);
            notify.error("No se pudieron cargar los recintos");
        } finally {
            setLoadingRecintos(false);
        }
    };

    const setAutoPassword = () => {
        const pwd = generatePassword(12);
        form.setFieldsValue({ password: pwd, temporaryPassword: false });
    };

    const onRutChange = (e) => {
        const next = formatRutSoft(e.target.value);
        const current = form.getFieldValue("rut");
        if (next !== current) {
            form.setFieldsValue({ rut: next });
        }
    };

    const onSubmit = async () => {
        try {
            setSaving(true);
            const values = await form.validateFields();

            const areaValue = showArea ? (values.area || null) : "global";


            const payload = {
                email: values.email,
                password: values.password,
                name: values.name,
                phone_number: values.phone_number || null,
                birthdate: values.birthdate ? dayjs(values.birthdate).format("YYYY-MM-DD") : null,
                address: values.address || null,
                rut: values.rut,
                role: values.role,
                temporaryPassword: false,
                area: areaValue,
                gender: values.gender || null,
                commune: values.commune || null,
                enfermedadesBase: values.enfermedadesBase || null,
            };

            await withNotify(
                Usuarios.Create(payload),
                {
                    loading: "Creando usuario...",
                    success: "Usuario creado correctamente",
                    error: "No se pudo crear el usuario, verifique los datos ingresados o que el usuario no exista",
                }
            );

            form.resetFields();
            window.location.href = "/admin/usuarios";
        } catch (error) {
            console.error("Error creando usuario:", error);
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="p-4 md:p-6 space-y-4">
            <div className="flex items-center justify-between">
                <Space>
                    <Button icon={<ArrowLeftOutlined />} onClick={() => window.history.back()}>
                        Volver
                    </Button>
                    <Title level={3} className="!mb-0">Crear Usuario</Title>
                </Space>
            </div>

            <Card>
                <Form
                    form={form}
                    layout="vertical"
                    initialValues={{
                        temporaryPassword: true,
                        role: "jefe_recinto",
                    }}
                >
                    {/* SECCIÓN: Datos Clave */}
                    <Divider orientation="left">Datos Clave</Divider>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <Form.Item
                            label="RUT"
                            name="rut"
                            rules={[
                                { required: true, message: "Ingrese el RUT" },
                                () => ({
                                    validator(_, value) {
                                        if (!value) return Promise.resolve();
                                        const strict = /^[0-9]{1,2}\.[0-9]{3}\.[0-9]{3}-[0-9K]$/;
                                        return strict.test(value)
                                            ? Promise.resolve()
                                            : Promise.reject("Formato inválido. Use: 12.345.678-9");
                                    },
                                }),
                            ]}
                            hasFeedback
                        >
                            <Input
                                placeholder="12.345.678-9"
                                onChange={onRutChange}
                                maxLength={12}
                                size="large"
                            />
                        </Form.Item>

                        <Form.Item
                            label="Email"
                            name="email"
                            rules={[
                                { required: true, message: "Ingrese el email" },
                                { type: "email", message: "Email inválido" }
                            ]}
                            hasFeedback
                        >
                            <Input
                                placeholder="correo@ejemplo.com"
                                size="large"
                            />
                        </Form.Item>

                        <Form.Item
                            label="Rol"
                            name="role"
                            rules={[{ required: true, message: "Seleccione un rol" }]}
                            hasFeedback
                        >
                            <Select
                                placeholder="Seleccione el rol del nuevo usuario"
                                options={ROLES}
                                size="large"
                            />
                        </Form.Item>

                        {showArea && (
                            <Form.Item
                                label="Área (Recinto)"
                                name="area"
                                tooltip="Solo se habilita cuando el rol seleccionado es 'Jefe de Recinto'"
                                rules={[{ required: true, message: "Seleccione un recinto" }]}
                                hasFeedback
                            >
                                <Select
                                    allowClear
                                    placeholder="Seleccione el recinto del usuario"
                                    options={recintos}
                                    size="large"
                                    loading={loadingRecintos}
                                
                                />
                            </Form.Item>
                        )}

                        <Form.Item
                            label="Contraseña"
                            name="password"
                            tooltip="Puede generarla automáticamente"
                            rules={[
                                { required: true, message: "Ingrese la contraseña" },
                                { min: 8, message: "Mínimo 8 caracteres" },
                                () => ({
                                    validator(_, value) {
                                        if (!value) return Promise.resolve();
                                        const ok =
                                            /[A-Z]/.test(value) &&
                                            /[a-z]/.test(value) &&
                                            /[0-9]/.test(value) &&
                                            /[^A-Za-z0-9]/.test(value);
                                        return ok
                                            ? Promise.resolve()
                                            : Promise.reject("Debe incluir mayúscula, minúscula, número y símbolo");
                                    },
                                }),
                            ]}
                            hasFeedback
                        >
                            <Input.Password
                                placeholder="Escriba una contraseña segura o genere una automáticamente"
                                size="large"
                            />
                        </Form.Item>

                        <Form.Item label="Acciones de contraseña">
                            <Space direction="vertical" className="w-full">
                                <Button
                                    icon={<ReloadOutlined />}
                                    onClick={setAutoPassword}
                                    block
                                    size="large"
                                >
                                    Generar contraseña segura
                                </Button>
                                <div className="text-xs text-gray-600 mt-2">
                                    ℹ️ La contraseña generada automáticamente será permanente, el usuario deberá cambiarla en su perfil.
                                </div>
                            </Space>
                        </Form.Item>
                    </div>

                    {/* SECCIÓN: Datos de Identificación Personal */}
                    <Divider orientation="left">Datos de Identificación Personal</Divider>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <Form.Item
                            label="Nombre completo"
                            name="name"
                            rules={[
                                { required: true, message: "Ingrese el nombre" },
                                { min: 3, message: "Mínimo 3 caracteres" },
                                {
                                    pattern: /^[a-zA-ZáéíóúÁÉÍÓÚñÑüÜ\s]+$/,
                                    message: "El nombre solo puede contener letras y espacios"
                                },
                                () => ({
                                    validator(_, value) {
                                        if (!value) return Promise.resolve();

                                        const palabras = value.trim().split(/\s+/);
                                        if (palabras.length < 2) {
                                            return Promise.reject("Debe ingresar al menos nombre y apellido");
                                        }

                                        return Promise.resolve();
                                    },
                                }),
                            ]}
                            hasFeedback
                            validateTrigger="onChange"
                        >
                            <Input
                                placeholder="Ingrese el nombre completo del usuario"
                                size="large"
                                maxLength={100}
                            />
                        </Form.Item>

                        <Form.Item
                            label="Fecha de nacimiento"
                            name="birthdate"
                            hasFeedback
                        >
                            <DatePicker
                                className="w-full"
                                format="DD/MM/YYYY"
                                placeholder="Seleccione la fecha de nacimiento"
                                disabledDate={(current) => {
                                    return current && current > dayjs().endOf('day');
                                }}
                                size="large"
                            />
                        </Form.Item>

                        <Form.Item
                            label="Género"
                            name="gender"
                            hasFeedback
                        >
                            <Select
                                allowClear
                                placeholder="Seleccione el género del usuario"
                                options={GENDERS}
                                size="large"
                            />
                        </Form.Item>

                        <Form.Item
                            label="Teléfono"
                            name="phone_number"
                            rules={[
                                () => ({
                                    validator(_, value) {
                                        if (!value) return Promise.resolve();
                                        return /^\+56[9]\d{8}$/.test(value)
                                            ? Promise.resolve()
                                            : Promise.reject("Formato: +56912345678");
                                    },
                                })
                            ]}
                            hasFeedback
                        >
                            <Input
                                placeholder="+56 9 1234 5678"
                                maxLength={12}
                                size="large"
                            />
                        </Form.Item>

                        <Form.Item
                            label="Dirección"
                            name="address"
                            hasFeedback
                        >
                            <Input
                                placeholder="Ingrese la dirección completa (calle y número)"
                                size="large"
                            />
                        </Form.Item>

                        <Form.Item
                            label="Comuna"
                            name="commune"
                            hasFeedback
                        >
                            <Select
                                allowClear
                                showSearch
                                placeholder="Seleccione la comuna de residencia del usuario"
                                options={COMMUNES}
                                optionFilterProp="label"
                                size="large"
                            />
                        </Form.Item>
                    </div>

                    {/* SECCIÓN: Información de Salud */}
                    <Divider orientation="left">Información de Salud</Divider>

                    <div className="grid grid-cols-1 gap-4">
                        <Form.Item
                            label="Enfermedades Base"
                            name="enfermedadesBase"
                            tooltip="Especifique si el usuario tiene condiciones médicas relevantes para actividades deportivas"
                            hasFeedback
                        >
                            <TextArea
                                rows={4}
                                placeholder="Ejemplo: Diabetes, Hipertensión, Asma, etc. (Opcional)"
                                maxLength={500}
                                showCount
                                size="large"
                            />
                        </Form.Item>
                    </div>

                    <Divider />

                    <Space className="mt-4">
                        <Button
                            type="primary"
                            icon={<SaveOutlined />}
                            loading={saving}
                            onClick={onSubmit}
                            size="large"
                        >
                            Crear usuario
                        </Button>
                        <Button
                            onClick={() => window.history.back()}
                            size="large"
                        >
                            Cancelar
                        </Button>
                    </Space>
                </Form>
            </Card>

            <Card title="Información importante" size="small" className="bg-blue-50 border-blue-200">
                <ul className="list-disc pl-5 space-y-2 text-sm">
                    <li>
                        El campo <strong>"Área"</strong> solo se habilita cuando el rol seleccionado es <strong>"Jefe de Recinto"</strong>.
                    </li>
                    <li>
                        La contraseña generada automáticamente será permanente. El usuario deberá modificarla en su perfil si lo desea.
                    </li>

                </ul>
            </Card>
        </div>
    );
}
